/* global budgetIsLocked processAjaxConfirmation */
import React, { useCallback, useEffect, useRef, useState } from 'react';

import BudgetTagsItemEditor from './BudgetTagsItemEditor';
import { budgetTagsErrorMessages, saveItemBudgetTags } from './itemBudgetTagsApi';

import Button from 'CommonComponents/Button';
import Modal from 'CommonComponents/Modal';
import ModalHeader from 'CommonComponents/ModalHeader';

import useModal from 'Hooks/useModal';

/**
 * Edits one template or pack item's budget tags from its header's item grid. Saves
 * straight away and hands the item's new grid cell to `onSaved`.
 *
 * `target` is the item being edited ({ id, name, packId }), or null while closed.
 */
export default function BudgetTagsItemModal({ id, url, target, editable, onClose, onSaved }) {
    const modal = useModal();
    const [errors, setErrors] = useState([]);
    const [saving, setSaving] = useState(false);
    const [ready, setReady] = useState(false);
    const payload = useRef(null);

    useEffect(() => {
        setErrors([]);
        target ? modal.show() : modal.hide();
    }, [target]);

    const handleChange = useCallback((value) => {
        payload.current = value;
        setReady(value !== null);
    }, []);

    const save = () => {
        setSaving(true);
        setErrors([]);
        saveItemBudgetTags(url, target.id, payload.current, target.packId || null)
            .then((data) => {
                onSaved(target.id, data.cell);
                onClose();
            })
            .catch((error) => setErrors(budgetTagsErrorMessages(error)))
            .finally(() => setSaving(false));
    };

    const handleSave = () => {
        if (!payload.current) {
            return;
        }
        typeof budgetIsLocked !== 'undefined' && budgetIsLocked ? processAjaxConfirmation(save) : save();
    };

    return (
        <Modal id={id} modal={modal}>
            <ModalHeader title={$t('Budget tags')} subtitle={target?.name} close={onClose} />
            <div className="tw-space-y-4">
                {target && (
                    <BudgetTagsItemEditor
                        key={`${target.id}-${target.packId || ''}`}
                        id={`${id}-tags`}
                        url={url}
                        itemId={target.id}
                        packId={target.packId || null}
                        disabled={!editable}
                        onChange={handleChange}
                    />
                )}
                {errors.map((message, index) => (
                    <p key={index} className="tw-text-red tw-text-sm tw-mb-0">
                        {message}
                    </p>
                ))}
            </div>
            <div className="modal__footer tw-justify-end tw-space-x-1.5">
                <Button variant="secondary" onClick={onClose} disabled={saving}>
                    {editable ? $t('Cancel') : $t('Close')}
                </Button>
                {editable && (
                    <Button variant="primary" onClick={handleSave} disabled={saving || !ready} loading={saving}>
                        {$t('Save')}
                    </Button>
                )}
            </div>
        </Modal>
    );
}
